import { useTransition, type Dispatch, type SetStateAction } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EllipsisVertical, ListChecks, MailIcon, MailOpenIcon } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type RowSelectionState,
} from '@tanstack/react-table';
import { capitalize } from 'lodash';
import { useNavigate } from 'react-router';

import { type ITasksQuery } from 'src/graphql/actions/tasks/tasks.query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import Paginator from '@/components/ui/paginator';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from 'src/lib/utils';
import { EmptyBlock } from '@/components/ui/empty-block';

import { eventTypeKeys, getTaskContent, setTaskUrl } from '../model';

import CriticalityTooltip from './CriticalityTooltip';
import TypeTooltip from './TypeTooltip';

dayjs.extend(relativeTime);

type ITaskNode = ITasksQuery['tasks']['nodes'][number];

type Props = {
  type: 'unread' | 'read';
  totalCount: number;
  data: ITasksQuery['tasks'];
  page: number;
  onPageChange: (page: number) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: Dispatch<SetStateAction<RowSelectionState>>;
  onMarkAsRead?: (taskIds: string[]) => void;
  onMarkAsUnread?: (taskIds: string[]) => void;
};

const columnHelper = createColumnHelper<ITaskNode>();

const columns = ({
  type,
  totalCount,
  onMarkAsRead,
  onMarkAsUnread,
}: {
  type: 'unread' | 'read';
  totalCount: number;
  onMarkAsRead?: (ids: string[]) => void;
  onMarkAsUnread?: (ids: string[]) => void;
}) => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        disabled={!totalCount}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    meta: {
      headerClassName: 'pr-0 w-8',
      cellClassName: 'align-top py-5.5 pr-0 w-8',
    },
  }),
  columnHelper.accessor('eventType', {
    header: () => `${capitalize(type)} tasks ${totalCount ? `(${totalCount})` : ''}`,
    cell: (props) => {
      const parentType = props.row.original.parentType;
      const payload = props.row.original.payload;
      const content = getTaskContent({
        eventType: props.getValue() as (typeof eventTypeKeys)[number],
        payload: props.row.original.payload,
        parentType: props.row.original.parentType,
      });

      if (!content) return null;

      const { title, description, icon: Icon } = content;

      return (
        <div className="flex items-start gap-2">
          {Icon && (
            <TypeTooltip type={parentType === 'Escrow' ? payload.escrow_type : parentType}>
              <div className="mt-[5px] shrink-0">
                <Icon className="size-4.5" />
              </div>
            </TypeTooltip>
          )}
          <div className="text-foreground grow">
            <span className="text-sm font-medium">{title}</span>
            <p className="text-xs">{description}</p>
          </div>
        </div>
      );
    },
    enableSorting: false,
    meta: {
      headerClassName: 'min-w-60',
      cellClassName: 'min-w-60',
    },
  }),
  columnHelper.accessor('createdAt', {
    header: '',
    cell: (props) => {
      const date = dayjs(props.getValue());
      const formatted = dayjs().diff(date, 'day') > 7 ? date.format('MMM D, YYYY') : date.fromNow();
      return <span className="text-sm text-foreground whitespace-nowrap">{formatted}</span>;
    },
    enableSorting: false,
    meta: {
      cellClassName: 'text-right',
    },
  }),
  columnHelper.accessor('criticality', {
    header: '',
    cell: (props) => <CriticalityTooltip type={props.getValue()} />,
    meta: {
      headerClassName: 'w-8',
      cellClassName: 'w-8 px-0 text-center',
    },
  }),
  columnHelper.accessor('id', {
    header: '',
    cell: (props) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {type === 'unread' && onMarkAsRead && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead([props.getValue()]);
              }}
            >
              <MailOpenIcon /> Mark as read
            </DropdownMenuItem>
          )}
          {type === 'read' && onMarkAsUnread && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsUnread([props.getValue()]);
              }}
            >
              <MailIcon /> Mark as unread
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    meta: {
      headerClassName: 'w-14',
      cellClassName: 'w-14 px-0 text-center',
    },
  }),
];

const TasksTable = ({
  type,
  data,
  page,
  totalCount,
  onPageChange,
  rowSelection,
  onRowSelectionChange,
  onMarkAsRead,
  onMarkAsUnread,
}: Props) => {
  const [isPending, startTransition] = useTransition();
  const navigate = useNavigate();

  const table = useReactTable({
    data: data.nodes,
    columns: columns({
      type,
      totalCount,
      onMarkAsRead,
      onMarkAsUnread,
    }),
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange,
    getRowId: (row) => row.id,
    state: { rowSelection },
  });

  const onRowClick =
    ({ id, ...rest }: { id: string } & Parameters<typeof setTaskUrl>[0]) =>
    () => {
      if (type === 'unread') onMarkAsRead?.([id]);
      const { path, state } = setTaskUrl(rest);
      navigate(path, { state });
    };

  return (
    <div className="space-y-4">
      <Card className="shadow-none">
        <Table isLoading={isPending}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="[&_[role=checkbox]:not([data-state=checked])]:bg-white">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {totalCount ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    'cursor-pointer [&:has([role=checkbox][data-state=unchecked])_[role=checkbox]]:bg-white',
                    type === 'read' && 'bg-muted',
                  )}
                  onClick={onRowClick({
                    id: row.original.id,
                    parentId: row.original.parentId,
                    parentType: row.original.parentType,
                    eventType: row.original.eventType,
                    payload: row.original.payload,
                    recordId: row.original.recordId,
                  })}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(cell.column.columnDef.meta?.cellClassName)}
                      onClick={
                        cell.column.id === 'select' || cell.column.id === 'actions'
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="p-6">
                  <EmptyBlock
                    icon={ListChecks}
                    title="You are up to date"
                    withCard={false}
                    classes={{
                      title: 'text-base',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Paginator
        currentPage={page}
        totalPages={data.pagesCount}
        onPageChange={(p) => startTransition(() => onPageChange(p))}
        showPreviousNext
        hideOnSinglePage
      />
    </div>
  );
};

export default TasksTable;
